/**
 * useStaffHold: the booking sheet's staff-hold rules (CC 0366, 0368 and the 1013d–1013j and 1014a
 * follow-ups) as one hook, so the queue console, Calendar Control and the floor calendar hold, refuse
 * and convert exactly as the sheet does. BookAppointmentSheet keeps its own copy for now (it is
 * merged and live); it can move onto this hook in a later cut with no behaviour change.
 *
 *   • One hold create in flight at a time. A pick made meanwhile queues; only the LATEST is kept.
 *     When the in-flight create answers, a hold that is no longer for the time on screen is
 *     released (and the release awaited) before the queued create is sent.
 *   • A hold answer for anything but the current pick, or after close/unmount, is released.
 *   • Close (active=false) and unmount drop the pick and the queue. A create in flight owns its
 *     hold: it is not released then, and the create's outcome decides (kept on success, released
 *     on failure once the screen is gone).
 *   • While a create is converting the hold, a new pick is ignored: the hold being converted is
 *     never released by a pick (CC 1014f review). Screens also disable their time controls then.
 *   • Refreshed a minute before it lapses; HOLD_EXPIRED on refresh = "no longer held".
 *   • APPOINTMENT_HOLD_CONFLICT: "just taken", no override. STAFF_BUSY_CONFLICT: list what it
 *     overlaps and offer "Book anyway" (acknowledgeCollisions), on the hold or on the create.
 *   • HOLD_EXPIRED / HOLD_INVALID from the create: the hold is gone; clear it and re-pick.
 */
import { useEffect, useRef, useState } from "react";
import { createAdminAppointmentHold, releaseAdminAppointmentHold, refreshAdminAppointmentHold } from "@v0y4ge/api-client";
import { holdIsForPick, holdIsGone } from "./booking-sheet-hold";

export type HoldInput = Omit<Parameters<typeof createAdminAppointmentHold>[0], "acknowledgeCollisions">;
export type StaffHold = { holdId: string; expiresAt: string; staffId: string; staffName: string | null; key: string };
export type Collision = { kind: string; start: string; end: string; ref?: string };
export type HoldRefusal =
  | { kind: "taken"; message: string }
  | { kind: "busy"; collisions: Collision[]; count: number; during: "hold" | "create" }
  | { kind: "lapsed" };

export const errCode = (e: unknown) => (e && typeof e === "object" ? String((e as { code?: unknown }).code ?? "") : "");
export const errParams = (e: unknown) => ((e && typeof e === "object" ? (e as { params?: unknown }).params : null) || {}) as Record<string, unknown>;
export const errMessage = (e: unknown) => (e instanceof Error ? e.message : String(e));
/** params.collisions is comma-joined "<kind>:<startUtc>/<endUtc>[:<ref>]". */
export function parseCollisions(raw: unknown): Collision[] {
  if (typeof raw !== "string" || !raw.trim()) return [];
  return raw.split(",").map((part) => {
    const p = part.trim(); const k = p.indexOf(":"); if (k < 0) return null;
    const kind = p.slice(0, k); const rest = p.slice(k + 1);
    const slash = rest.indexOf("/"); if (slash < 0) return null;
    const start = rest.slice(0, slash); const tail = rest.slice(slash + 1);
    const m = tail.match(/^(\S+?Z|\S+?[+-]\d{2}:\d{2})(?::(.+))?$/);
    return m ? { kind, start, end: m[1], ...(m[2] ? { ref: m[2] } : {}) } : { kind, start, end: tail };
  }).filter((c): c is Collision => !!c);
}
export const busyRefusal = (e: unknown, during: "hold" | "create"): HoldRefusal => {
  const p = errParams(e);
  return { kind: "busy", collisions: parseCollisions(p.collisions), count: Number(p.collisionCount) || 0, during };
};

type HoldReq = { key: string; ack: boolean; input: Parameters<typeof createAdminAppointmentHold>[0] };
export type ConvertResult<T> =
  | { ok: true; value: T }
  | { ok: false; handled: boolean; error: unknown };

export function useStaffHold({ active, onTaken }: { active: boolean; onTaken?: () => void }) {
  const [hold, setHold] = useState<StaffHold | null>(null);
  const [holding, setHolding] = useState(false);
  const [refusal, setRefusal] = useState<HoldRefusal | null>(null);
  const [acked, setAcked] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const holdRef = useRef<StaffHold | null>(null);
  holdRef.current = hold;
  const keyRef = useRef("");
  const genRef = useRef(0);
  const inFlightRef = useRef(false);
  const queuedRef = useRef<HoldReq | null>(null);
  const lastReqRef = useRef<HoldReq | null>(null);
  const mountedRef = useRef(true);
  const savingRef = useRef(false);
  const goneDuringSaveRef = useRef(false);
  const convertedRef = useRef(false);
  const onTakenRef = useRef(onTaken);
  onTakenRef.current = onTaken;

  const dropHold = () => { holdRef.current = null; if (mountedRef.current) setHold(null); };
  const releaseHeld = () => {
    const h = holdRef.current; dropHold();
    if (h) void releaseAdminAppointmentHold(h.holdId).catch(() => { /* it lapses on its own */ });
  };
  const taken = (e: unknown) => {
    setRefusal({ kind: "taken", message: errMessage(e) });
    keyRef.current = ""; queuedRef.current = null;
    onTakenRef.current?.();
  };

  const runHold = async (req: HoldReq): Promise<void> => {
    const { key, ack } = req;
    inFlightRef.current = true;
    if (mountedRef.current) { setHolding(true); setRefusal(null); }
    try {
      const h = await createAdminAppointmentHold(req.input);
      if (!mountedRef.current || !holdIsForPick(key, keyRef.current)) {
        await releaseAdminAppointmentHold(h.holdId).catch(() => { /* it lapses on its own */ });
      } else {
        const next: StaffHold = { holdId: h.holdId, expiresAt: h.expiresAt, staffId: h.staffId, staffName: h.staffName, key };
        holdRef.current = next; setHold(next);
        if (ack) setAcked(true);
      }
    } catch (e) {
      if (mountedRef.current && holdIsForPick(key, keyRef.current)) {
        const code = errCode(e);
        if (code === "APPOINTMENT_HOLD_CONFLICT") taken(e);
        else if (code === "STAFF_BUSY_CONFLICT") setRefusal(busyRefusal(e, "hold"));
        else setErr(errMessage(e));
      }
    } finally {
      inFlightRef.current = false;
      const queued = queuedRef.current; queuedRef.current = null;
      if (queued && mountedRef.current && holdIsForPick(queued.key, keyRef.current)) void runHold(queued);
      else if (mountedRef.current && (holdIsForPick(key, keyRef.current) || !keyRef.current)) setHolding(false);
    }
  };
  const send = (req: HoldReq) => {
    lastReqRef.current = req;
    if (inFlightRef.current) { queuedRef.current = req; setHolding(true); setRefusal(null); return; }
    void runHold(req);
  };

  /** A new pick: release what is held, then hold this one (or queue it behind the create in flight).
   *  `baseKey` is slotKey(date, slot); the hook adds a generation so a repeat of the same time never
   *  matches an older answer. */
  const pick = (baseKey: string, input: HoldInput) => {
    if (!active || !baseKey || savingRef.current) return;
    releaseHeld(); setAcked(false); setRefusal(null); setErr(null);
    genRef.current += 1;
    const key = baseKey + "#" + genRef.current;
    keyRef.current = key;
    send({ key, ack: false, input });
  };
  /** "Book anyway" on a busy refusal at hold time: the same hold again, with acknowledgeCollisions. */
  const holdAnyway = () => {
    const last = lastReqRef.current;
    if (!last || !holdIsForPick(last.key, keyRef.current)) return;
    send({ key: last.key, ack: true, input: { ...last.input, acknowledgeCollisions: true } });
  };
  /** The pick was cleared by the screen (new day, filter, service): drop it and release. */
  const clear = () => {
    keyRef.current = ""; queuedRef.current = null; setHolding(false);
    if (!savingRef.current) releaseHeld();
  };
  /** Confirm needs a hold for exactly the time on screen. */
  const isHeldFor = (baseKey: string) =>
    !!hold && !!baseKey && hold.key === keyRef.current && keyRef.current.startsWith(baseKey + "#");

  /** Run the create that converts the hold. `create` receives the hold id (or null when the screen
   *  books without one) and whether "Book anyway" was given. Refusals the hook owns are shown by the
   *  hook (`handled: true`); anything else comes back for the screen to word. */
  const convert = async <T,>(create: (holdId: string | null, ack: boolean) => Promise<T>, ack: boolean): Promise<ConvertResult<T>> => {
    savingRef.current = true; goneDuringSaveRef.current = false;
    if (mountedRef.current) { setRefusal(null); setErr(null); }
    const heldId = holdRef.current?.holdId ?? null;
    try {
      const value = await create(heldId, ack);
      savingRef.current = false; convertedRef.current = true;
      dropHold();
      return { ok: true, value };
    } catch (e) {
      savingRef.current = false;
      const code = errCode(e);
      if (goneDuringSaveRef.current || !mountedRef.current) {
        if (heldId && code !== "APPOINTMENT_HOLD_CONFLICT" && !holdIsGone(code)) void releaseAdminAppointmentHold(heldId).catch(() => { /* lapses */ });
        dropHold();
        return { ok: false, handled: true, error: e };
      }
      if (code === "APPOINTMENT_HOLD_CONFLICT") { dropHold(); taken(e); return { ok: false, handled: true, error: e }; }
      if (holdIsGone(code)) {
        dropHold(); setAcked(false); keyRef.current = ""; queuedRef.current = null;
        setRefusal({ kind: "lapsed" }); onTakenRef.current?.();
        return { ok: false, handled: true, error: e };
      }
      if (code === "STAFF_BUSY_CONFLICT") { setRefusal(busyRefusal(e, "create")); return { ok: false, handled: true, error: e }; }
      return { ok: false, handled: false, error: e };
    }
  };

  /* Close: the pick, its key and the queue go; a create in flight keeps its hold. */
  useEffect(() => {
    if (active) return;
    genRef.current += 1; keyRef.current = ""; queuedRef.current = null; setHolding(false);
    if (savingRef.current) goneDuringSaveRef.current = true; else if (!convertedRef.current) releaseHeld();
  }, [active]); // eslint-disable-line react-hooks/exhaustive-deps
  /* Unmount does exactly what close does. */
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false; genRef.current += 1; keyRef.current = ""; queuedRef.current = null;
      if (savingRef.current) goneDuringSaveRef.current = true; else if (!convertedRef.current) releaseHeld();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  /* Refresh a minute before it lapses. */
  useEffect(() => {
    if (!hold) return;
    const due = Date.parse(hold.expiresAt) - Date.now() - 60_000;
    const timer = window.setTimeout(() => {
      void refreshAdminAppointmentHold(hold.holdId).then((h) => {
        if (holdRef.current?.holdId !== hold.holdId) return;
        const next: StaffHold = { holdId: h.holdId, expiresAt: h.expiresAt, staffId: h.staffId, staffName: h.staffName, key: hold.key };
        holdRef.current = next; setHold(next);
      }).catch((e) => {
        if (holdRef.current?.holdId !== hold.holdId) return;
        if (errCode(e) === "HOLD_EXPIRED") { dropHold(); keyRef.current = ""; setRefusal({ kind: "lapsed" }); onTakenRef.current?.(); }
      });
    }, Math.max(0, due));
    return () => window.clearTimeout(timer);
  }, [hold]);

  return {
    hold, holding, refusal, acked, err, setErr, setRefusal, setAcked,
    pick, holdAnyway, clear, isHeldFor, convert,
    /** True while a create is converting the hold. */
    isSaving: () => savingRef.current,
  };
}
export type StaffHoldApi = ReturnType<typeof useStaffHold>;
